import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Sheet } from '../../components/ui/Sheet';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { TextField } from '../../components/ui/TextField';
import { Toggle } from '../../components/ui/Toggle';
import { Button } from '../../components/ui/Button';
import { DEFAULT_SIGNAL_COLOR, DEFAULT_SIGNAL_EMOJI, SignalColorPicker, SignalEmojiPicker } from '../../components/signal/SignalEditorFields';
import { searchTemplates, fetchTemplate } from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';
import { resolveNickname } from '../../lib/mappingNickname';
import { usePersistedState } from '../../lib/usePersistedState';
import { useStore } from '../../store/StoreContext';
import { uid, type Template } from '../../types';

const NEW_TEMPLATE_TITLE_FALLBACK = 'New signals';

// The owner-only "idea" affordance offered on a plain text message or a
// signal message that isn't in any of this channel's templates yet (see
// MessagesScreen.tsx's isUnknownSignal) - "you don't have this yet, want
// to save it as a real, reusable signal?" This is a full editor, not a
// one-tap save: the message only seeds the starting name/value, since a
// raw incoming string (an integration label, a hand-typed note) rarely
// wants to become the signal's name/emoji/color verbatim.
export function SaveAsSignalSheet({
  open,
  onClose,
  initialName,
  initialValue,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  initialName: string;
  initialValue?: string;
  // Fired after a successful save, with the template the signal landed in
  // (existing or newly created) - the caller decides what to do with that
  // (e.g. offer "attach this template to the channel").
  onSaved: (template: Template) => void;
}) {
  const { addTemplate, updateTemplate, profile } = useStore();
  const [name, setName] = useState(initialName);
  const [value, setValue] = useState(initialValue ?? '');
  const [emoji, setEmoji] = useState(DEFAULT_SIGNAL_EMOJI);
  const [colorHex, setColorHex] = useState(DEFAULT_SIGNAL_COLOR);
  const [promptForValue, setPromptForValue] = useState(false);
  const [target, setTarget] = useState<'existing' | 'new'>('existing');

  // "Add to existing template" - searched, not a flat <select>, same
  // rationale as TemplatePickerScreen (an account's template count isn't
  // bounded, so this never assumes the full set fits client-side).
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Template[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const requestId = useRef(0);

  const [newTitle, setNewTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The template this account last saved a signal into (nickname, survives
  // reload) - once it's set, every later open preselects it instead of
  // making someone re-pick the same target every time: configure it once,
  // then adding another signal is a single click on Save.
  const [lastTemplateNickname, setLastTemplateNickname] = usePersistedState<string>('saveAsSignal.lastTemplate', '');

  // Reseed every time the sheet opens for a (possibly different) message -
  // stale state from a previous open would otherwise leak through.
  useEffect(() => {
    if (!open) return;
    setName(initialName);
    // A message that already carries a value (an integration reading, a
    // typed amount) is usually one sample of something that varies - "ask
    // each time" is the more useful default than baking this one instance
    // in as a permanent static value. The field itself starts blank either
    // way (see the toggle's onChange below) - the "Use this message's
    // value" chip offers it back in one click for whichever mode is kept.
    const hasValue = !!(initialValue ?? '').trim();
    setValue('');
    setEmoji(DEFAULT_SIGNAL_EMOJI);
    setColorHex(DEFAULT_SIGNAL_COLOR);
    setPromptForValue(hasValue);
    setTarget('existing');
    setQuery('');
    setResults([]);
    setNewTitle('');
    setError('');
    // Preselect the last template this account saved into, if any -
    // otherwise leave it unset so a first-ever use still has to pick one.
    if (lastTemplateNickname) {
      queryClient
        .query({ queryKey: ['channelTemplate', lastTemplateNickname], queryFn: () => fetchTemplate(lastTemplateNickname) })
        .then((t) => setSelectedTemplate(t))
        .catch(() => setSelectedTemplate(null));
    } else {
      setSelectedTemplate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialName, initialValue]);

  useEffect(() => {
    if (!open || target !== 'existing') return;
    const thisRequest = ++requestId.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const page = await queryClient.query({
          queryKey: queryKeys.templateSearch('mine', query, 0),
          queryFn: () => searchTemplates('mine', query),
        });
        const hydrated = (await Promise.all(page.data.map((n) => fetchTemplate(n)))).filter((t): t is Template => t !== null);
        if (requestId.current === thisRequest) setResults(hydrated);
      } catch {
        if (requestId.current === thisRequest) setResults([]);
      } finally {
        if (requestId.current === thisRequest) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [open, target, query]);

  const canSave = name.trim() !== '' && (target === 'new' || !!selectedTemplate) && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const signal = {
        id: uid(),
        name: name.trim(),
        value: value.trim() || undefined,
        promptForValue: promptForValue || undefined,
        colorHex,
        emoji: emoji || DEFAULT_SIGNAL_EMOJI,
      };
      if (target === 'existing' && selectedTemplate) {
        const updated: Template = { ...selectedTemplate, signals: [...selectedTemplate.signals, signal] };
        await updateTemplate(updated);
        setLastTemplateNickname(updated.nickname);
        onSaved(updated);
      } else {
        const nickname = resolveNickname('', '@signals', uid().slice(0, 8));
        const created: Template = {
          id: uid(),
          title: newTitle.trim() || NEW_TEMPLATE_TITLE_FALLBACK,
          nickname,
          isPublic: false,
          colorHex,
          emoji,
          avatarImageKey: '',
          signals: [signal],
          likes: 0,
          liked: false,
          usages: 0,
          ownerId: profile.nickname,
        };
        await addTemplate(created);
        setLastTemplateNickname(created.nickname);
        onSaved(created);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save this signal.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Save as signal">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <SignalEmojiPicker emoji={emoji} colorHex={colorHex} onEmojiChange={setEmoji} />
          <SignalColorPicker colorHex={colorHex} onColorChange={setColorHex} />
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} className="flex-1" />
        </div>
        <TextField
          label={promptForValue ? 'Default value (optional, prefilled at send time)' : 'Value (optional)'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        {/* Only shown once there's a captured value to offer back - restores
            it in one click after the toggle below cleared the field, since
            it's still what MessagesScreen's pendingValue prompt prefills
            from (TemplateSignal.value doubles as the "ask each time"
            default) - so the number this message carried stays one tap
            away instead of needing to be retyped from memory. */}
        {!!initialValue && value !== initialValue && (
          <button
            type="button"
            onClick={() => setValue(initialValue)}
            className="self-start text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Use this message's value ({initialValue})
          </button>
        )}
        <Toggle
          label="Ask for a value each time it's sent"
          checked={promptForValue}
          onChange={(checked) => {
            setPromptForValue(checked);
            // Turning this on clears the static field - "always ask" and a
            // fixed value are usually mutually exclusive intents, and an
            // empty field better signals that a value is expected at send
            // time rather than looking like a forgotten leftover. The chip
            // above puts the message's own value straight back with one
            // click, so nothing is actually lost.
            if (checked) setValue('');
          }}
        />

        <SegmentedControl
          options={[
            { value: 'existing' as const, label: 'Add to a schema' },
            { value: 'new' as const, label: 'New schema' },
          ]}
          value={target}
          onChange={setTarget}
        />

        {target === 'existing' ? (
          <div className="flex flex-col gap-2">
            {/* Preselected from lastTemplateNickname above - called out here
                since it may not be among the first search results shown
                below, and "just tap Save" only reads as one click if it's
                obvious a target is already chosen. */}
            {selectedTemplate && (
              <span className="text-xs text-black/50 dark:text-white/50">
                Selected: <span className="font-medium text-black/70 dark:text-white/70">{selectedTemplate.title || selectedTemplate.nickname}</span>
              </span>
            )}
            <TextField placeholder="Search my schemas" value={query} onChange={(e) => setQuery(e.target.value)} />
            <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto">
              {searching && <span className="text-sm text-black/40 dark:text-white/40 py-2 text-center">Searching…</span>}
              {!searching && results.length === 0 && (
                <span className="text-sm text-black/40 dark:text-white/40 py-2 text-center">
                  {query ? 'No schemas match that search.' : 'No schemas yet - create one instead.'}
                </span>
              )}
              {!searching &&
                results.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTemplate(t)}
                    className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left ${
                      selectedTemplate?.id === t.id
                        ? 'border-blue-500 bg-blue-500/5'
                        : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                  >
                    <span
                      className="w-8 h-8 rounded-full flex items-center justify-center text-base shrink-0"
                      style={{ background: `${t.colorHex}1f` }}
                    >
                      {t.emoji}
                    </span>
                    <span className="flex-1 min-w-0 truncate text-sm font-medium">{t.title || t.nickname}</span>
                    {selectedTemplate?.id === t.id && <Check size={16} className="text-blue-600 dark:text-blue-400 shrink-0" />}
                  </button>
                ))}
            </div>
          </div>
        ) : (
          <TextField label="Schema title" placeholder={NEW_TEMPLATE_TITLE_FALLBACK} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}
        <Button onClick={save} disabled={!canSave} loading={saving}>
          Save signal
        </Button>
      </div>
    </Sheet>
  );
}
